/**
 * <site-header> — mobile nav toggle + search overlay with predictive
 * search. Uses the plain /search/suggest.json endpoint (not the section-
 * rendering variant) so results don't need a dedicated predictive-search
 * section; product prices come back pre-formatted for the shop's active
 * currency/market.
 */
class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.menuToggle = this.querySelector('[data-menu-toggle]');
    this.nav = this.querySelector('[data-site-nav]');
    this.searchToggle = this.querySelector('[data-search-toggle]');
    this.searchOverlay = this.querySelector('[data-search-overlay]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.searchResults = this.querySelector('[data-search-results]');

    this.menuToggle?.addEventListener('click', () => this.toggleMenu());
    this.searchToggle?.addEventListener('click', () => this.openSearch());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-close]')) this.closeSearch();
    });
    this.searchInput?.addEventListener('input', this.debounce(this.onSearchInput.bind(this), 250));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.closeSearch();
      this.closeMenu();
    });
  }

  toggleMenu() {
    const isOpen = this.nav.classList.toggle('is-open');
    this.menuToggle.setAttribute('aria-expanded', String(isOpen));
    document.body.classList.toggle('nav-open', isOpen);
  }

  closeMenu() {
    this.nav?.classList.remove('is-open');
    this.menuToggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('nav-open');
  }

  openSearch() {
    this.searchOverlay.classList.add('is-open');
    this.searchOverlay.setAttribute('aria-hidden', 'false');
    this.searchInput?.focus();
  }

  closeSearch() {
    this.searchOverlay?.classList.remove('is-open');
    this.searchOverlay?.setAttribute('aria-hidden', 'true');
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  async onSearchInput(event) {
    const query = event.target.value.trim();
    if (query.length < 2) {
      this.searchResults.innerHTML = '';
      return;
    }

    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;
      const response = await fetch(url);
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      this.renderResults(products);
    } catch (error) {
      // Non-fatal: leave last results in place.
    }
  }

  renderResults(products) {
    if (!products.length) {
      this.searchResults.innerHTML = `<p class="predictive-search__empty">${
        window.themeStrings?.noResults || 'No results found'
      }</p>`;
      return;
    }

    this.searchResults.innerHTML = products.map((product) => this.renderResultItem(product)).join('');
  }

  renderResultItem(product) {
    const title = this.escapeHtml(product.title || '');
    const url = this.escapeAttr(product.url || '#');
    const price = this.escapeHtml(product.price || '');
    const image = product.image ? this.escapeAttr(product.image) : '';

    return `
      <a href="${url}" class="predictive-search__item">
        ${image ? `<img src="${image}" alt="" width="48" height="60" loading="lazy">` : ''}
        <span class="predictive-search__item-text">
          <span class="predictive-search__title">${title}</span>
          <span class="predictive-search__price">${price}</span>
        </span>
      </a>`;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

customElements.define('site-header', SiteHeader);
